import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Grid Overlay Camera — live camera with selectable composition grids.
// Live preview only; user captures via their native camera app.

const GRIDS = [
  { id: "thirds",    label: "Rule of thirds" },
  { id: "golden",    label: "Golden ratio" },
  { id: "center",    label: "Center crosshair" },
  { id: "diagonal",  label: "Diagonal lines" },
  { id: "fibonacci", label: "Fibonacci spiral" },
  { id: "custom",    label: "Custom grid" },
];

const PHI = 1.618033988;

export default function GridOverlayCamera() {
  const [state, setState] = useState("idle");
  const [grid, setGrid] = useState("thirds");
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const enable = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setState("unsupported"); return; }
    if (!window.isSecureContext) { setState("insecure"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      // V57.6: srcObject attachment moved to post-render useEffect.
      // The <video> element is conditionally rendered (state === "ok")
      // and isn't in the DOM at this point — assigning srcObject here
      // would silently no-op on a null ref.
      setState("ok");
    } catch (err) {
      // V57.5: differentiate error types so the UI is honest
      if (err && err.name === "NotAllowedError") setState("denied");
      else if (err && err.name === "NotFoundError") setState("nodevices");
      else setState("denied");
    }
  };

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  // V57.6: attach stream after the <video> element renders.
  useEffect(() => {
    if (state === "ok" && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [state]);

  return (
    <ToolsShell title="Grid Overlay Camera">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Live camera with composition grids — rule of thirds, golden ratio, fibonacci spiral. Use your native camera app to capture.
          </p>
          <button onClick={enable} style={primaryBtn}>Enable camera</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Camera" />
        </div>
      )}

      {state === "ok" && (
        <>
          <div style={{ position: "relative", width: "100%", maxWidth: 600, aspectRatio: "3 / 4", background: "#000", borderRadius: 12, overflow: "hidden", margin: "0 auto" }}>
            <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            {/* Grid overlay */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
            >
              {grid === "thirds" && (
                <g stroke="rgba(255,255,255,0.6)" strokeWidth="0.3">
                  <line x1="33.33" y1="0" x2="33.33" y2="100" />
                  <line x1="66.67" y1="0" x2="66.67" y2="100" />
                  <line x1="0" y1="33.33" x2="100" y2="33.33" />
                  <line x1="0" y1="66.67" x2="100" y2="66.67" />
                </g>
              )}
              {grid === "golden" && (
                <g stroke="rgba(255,255,255,0.6)" strokeWidth="0.3">
                  {/* Golden ratio: divide at 1/phi = 0.382 and 1-1/phi = 0.618 */}
                  <line x1={100 / PHI} y1="0" x2={100 / PHI} y2="100" />
                  <line x1={100 - 100 / PHI} y1="0" x2={100 - 100 / PHI} y2="100" />
                  <line x1="0" y1={100 / PHI} x2="100" y2={100 / PHI} />
                  <line x1="0" y1={100 - 100 / PHI} x2="100" y2={100 - 100 / PHI} />
                </g>
              )}
              {grid === "center" && (
                <g stroke="rgba(255,255,255,0.7)" strokeWidth="0.3">
                  <line x1="50" y1="0" x2="50" y2="100" />
                  <line x1="0" y1="50" x2="100" y2="50" />
                  <circle cx="50" cy="50" r="3" fill="none" />
                </g>
              )}
              {grid === "diagonal" && (
                <g stroke="rgba(255,255,255,0.6)" strokeWidth="0.3">
                  <line x1="0" y1="0" x2="100" y2="100" />
                  <line x1="0" y1="100" x2="100" y2="0" />
                </g>
              )}
              {grid === "fibonacci" && <FibonacciSpiral />}
              {grid === "custom" && (
                <g stroke="rgba(255,255,255,0.5)" strokeWidth="0.3">
                  {Array.from({ length: cols - 1 }).map((_, i) => (
                    <line key={`v${i}`} x1={(100 * (i + 1)) / cols} y1="0" x2={(100 * (i + 1)) / cols} y2="100" />
                  ))}
                  {Array.from({ length: rows - 1 }).map((_, i) => (
                    <line key={`h${i}`} x1="0" y1={(100 * (i + 1)) / rows} x2="100" y2={(100 * (i + 1)) / rows} />
                  ))}
                </g>
              )}
            </svg>
          </div>

          {/* Grid type selector */}
          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {GRIDS.map((g) => (
              <button
                key={g.id}
                onClick={() => setGrid(g.id)}
                style={{
                  background: grid === g.id ? TOOLS_ACCENT : "#1C1E2E",
                  color: grid === g.id ? "#1A0604" : "#9A9DBE",
                  border: "none", borderRadius: 8,
                  padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                {g.label}
              </button>
            ))}
          </div>

          {grid === "custom" && (
            <div style={{ marginTop: 12, display: "flex", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#9A9DBE", display: "block", marginBottom: 4 }}>Rows: {rows}</label>
                <input type="range" min="2" max="10" value={rows} onChange={(e) => setRows(parseInt(e.target.value, 10))} style={{ width: "100%", accentColor: TOOLS_ACCENT }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#9A9DBE", display: "block", marginBottom: 4 }}>Columns: {cols}</label>
                <input type="range" min="2" max="10" value={cols} onChange={(e) => setCols(parseInt(e.target.value, 10))} style={{ width: "100%", accentColor: TOOLS_ACCENT }} />
              </div>
            </div>
          )}

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            Place important subjects on the grid intersections for stronger composition. Capture with your native camera app.
          </p>
        </>
      )}
    </ToolsShell>
  );
}

function FibonacciSpiral() {
  // Approximate golden spiral with 4 quarter-circles in nested squares.
  // For a 100×62 viewport (golden ratio), squares of size: 62, 38.2, 23.6, 14.6
  return (
    <g stroke="rgba(255,255,255,0.6)" strokeWidth="0.3" fill="none">
      {/* Bounding rectangle */}
      <rect x="0" y="0" width="100" height="62" />
      {/* Inner squares dividing */}
      <line x1="62" y1="0" x2="62" y2="62" />
      <line x1="62" y1="23.6" x2="100" y2="23.6" />
      <line x1="76.6" y1="23.6" x2="76.6" y2="62" />
      <line x1="76.6" y1="41.4" x2="100" y2="41.4" />
      {/* Spiral arcs (quarter-circles) */}
      <path d="M 0 0 A 62 62 0 0 1 62 62" />
      <path d="M 62 62 A 38 38 0 0 1 100 24" />
      <path d="M 100 24 A 23.6 23.6 0 0 1 76.6 0.4" />
      <path d="M 76.6 41.4 A 14.6 14.6 0 0 1 91.2 56" />
    </g>
  );
}
